import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { auth } from '../../firebase/config';
import { getApiBaseUrl } from '../../utils/apiConfig';

const FEATURE_LABELS = {
  chat: '채팅',
  saenggibu: '생기부 생성',
  inspector: '생기부 검사',
  scoring: '채점',
  assessment_plan: '계획서',
};

const FEATURE_COLORS = {
  chat: '#3182f6',
  saenggibu: '#22c55e',
  inspector: '#f59e0b',
  scoring: '#8b5cf6',
  assessment_plan: '#ef4444',
};

const PERIOD_OPTIONS = [
  { key: '7d', label: '1주' },
  { key: '30d', label: '1달' },
  { key: '365d', label: '1년' },
  { key: 'all', label: '전체' },
  { key: 'custom', label: '직접 지정' },
];

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f8f9fa',
    color: '#191f28',
    fontFamily: "'Pretendard Variable', Pretendard, 'Noto Sans KR', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
  },
  container: {
    width: 'min(1180px, calc(100% - 40px))',
    margin: '0 auto',
    padding: '36px 0 72px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
    alignItems: 'flex-start',
    marginBottom: 24,
  },
  title: {
    margin: 0,
    fontSize: 30,
    fontWeight: 800,
    letterSpacing: '-0.03em',
  },
  subtitle: {
    margin: '8px 0 0',
    color: '#6b7280',
    fontSize: 14,
    lineHeight: 1.5,
  },
  actions: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  button: {
    minHeight: 42,
    padding: '0 16px',
    border: 0,
    borderRadius: 12,
    background: '#3182f6',
    color: '#fff',
    fontWeight: 800,
    cursor: 'pointer',
  },
  ghostButton: {
    minHeight: 42,
    padding: '0 16px',
    border: 0,
    borderRadius: 12,
    background: '#e5e8eb',
    color: '#374151',
    fontWeight: 800,
    cursor: 'pointer',
  },
  select: {
    minHeight: 42,
    padding: '0 12px',
    border: '1px solid #d1d5db',
    borderRadius: 12,
    background: '#fff',
    color: '#374151',
    fontWeight: 700,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap: 12,
    marginBottom: 20,
  },
  card: {
    padding: 18,
    borderRadius: 18,
    background: '#fff',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  },
  label: {
    color: '#8b95a1',
    fontSize: 13,
    fontWeight: 700,
  },
  value: {
    marginTop: 8,
    fontSize: 26,
    fontWeight: 800,
  },
  section: {
    marginTop: 18,
    padding: 22,
    borderRadius: 20,
    background: '#fff',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  },
  sectionTitle: {
    margin: '0 0 6px',
    fontSize: 20,
    fontWeight: 800,
  },
  note: {
    margin: '0 0 16px',
    color: '#6b7280',
    fontSize: 13,
    lineHeight: 1.5,
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
  },
  th: {
    padding: '11px 10px',
    borderBottom: '1px solid #e5e8eb',
    color: '#6b7280',
    textAlign: 'left',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '12px 10px',
    borderBottom: '1px solid #f2f4f6',
    whiteSpace: 'nowrap',
  },
  num: {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },
  codeBlock: {
    margin: 0,
    padding: 14,
    borderRadius: 14,
    background: '#f2f4f6',
    color: '#374151',
    fontSize: 12,
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
  },
  chart: {
    width: '100%',
    height: 320,
    marginBottom: 18,
  },
  error: {
    margin: '16px 0',
    padding: 14,
    borderRadius: 14,
    background: '#fff1f1',
    color: '#dc2626',
    fontWeight: 700,
  },
  warning: {
    margin: '16px 0',
    padding: 14,
    borderRadius: 14,
    background: '#fffbeb',
    color: '#92400e',
    fontSize: 13,
    lineHeight: 1.6,
  },
  methodGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: 12,
    marginBottom: 16,
  },
  methodCard: {
    padding: 16,
    borderRadius: 16,
    background: '#f8f9fa',
  },
  methodTitle: {
    margin: '0 0 8px',
    fontSize: 15,
    fontWeight: 800,
  },
  methodList: {
    margin: 0,
    paddingLeft: 18,
    color: '#4b5563',
    fontSize: 13,
    lineHeight: 1.7,
  },
};

const fmt = (value) => (value || 0).toLocaleString('ko-KR');

const UsageDashboard = () => {
  const navigate = useNavigate();
  const [user, authLoading] = useAuthState(auth);
  const [weekly, setWeekly] = useState(null);
  const [chat, setChat] = useState(null);
  const [logQuery, setLogQuery] = useState(null);
  const [revenueBasis, setRevenueBasis] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('30d');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchWithAuth = useCallback(async (path) => {
    const token = await user.getIdToken();
    const res = await fetch(`${getApiBaseUrl()}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.detail || `HTTP ${res.status}`);
    }
    return res.json();
  }, [user]);

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        period,
        source: 'production',
      });
      if (period === 'custom') {
        if (startDate) params.set('start_date', startDate);
        if (endDate) params.set('end_date', endDate);
      }
      const safeFetch = async (path) => {
        try {
          return await fetchWithAuth(path);
        } catch (err) {
          return { success: false, available: false, reason: err.message || String(err) };
        }
      };
      const [weeklyData, chatData, logData, basisData] = await Promise.all([
        fetchWithAuth(`/api/admin/metrics/usage/weekly?${params.toString()}`),
        fetchWithAuth(`/api/admin/metrics/chat/categories?${params.toString()}`),
        safeFetch('/api/admin/metrics/runtime-log-query?days=7'),
        fetchWithAuth('/api/admin/metrics/ir/revenue-basis'),
      ]);
      setWeekly(weeklyData);
      setChat(chatData);
      setLogQuery(logData);
      setRevenueBasis(basisData);
    } catch (e) {
      setError(e.message || '운영 지표를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [endDate, fetchWithAuth, period, startDate, user]);

  useEffect(() => {
    if (!authLoading && user) {
      loadData();
    }
  }, [authLoading, loadData, user]);

  const totals = useMemo(() => weekly?.totals || {}, [weekly]);
  const weeklyChartRows = useMemo(() => (
    (weekly?.rows || []).map((row) => ({
      week: row.week,
      label: row.label,
      chat: row.features?.chat?.events || 0,
      saenggibu: row.features?.saenggibu?.events || 0,
      inspector: row.features?.inspector?.events || 0,
      scoring: row.features?.scoring?.events || 0,
      assessment_plan: row.features?.assessment_plan?.events || 0,
    }))
  ), [weekly]);
  const chatChartRows = useMemo(() => (
    (chat?.rows || [])
      .filter((row) => row.category !== 'legacy_or_unknown')
      .slice(0, 9)
      .map((row) => ({
        label: row.label,
        conversations: row.conversations || 0,
        unique_users: row.unique_users || 0,
      }))
  ), [chat]);
  const warnings = useMemo(() => [
    ...(weekly?.warnings || []).map((message) => `주간 사용량: ${message}`),
    ...(chat?.warnings || []).map((message) => `채팅 카테고리: ${message}`),
  ], [chat, weekly]);
  const downloadExcel = useCallback(async () => {
    if (!weekly && !chat) return;
    const XLSX = await import('xlsx');
    const wb = XLSX.utils.book_new();

    if (weekly?.rows?.length) {
      const rows = [];
      weekly.rows.forEach((week) => {
        Object.entries(week.features || {}).forEach(([feature, data]) => {
          rows.push({
            '주차': week.week,
            '기간': week.label,
            '기능': FEATURE_LABELS[feature] || feature,
            '이벤트 수': data.events || 0,
            '단위 수': data.units || 0,
            '고유 사용자': data.unique_users || 0,
          });
        });
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), '주간 사용량');
    }

    if (chat?.rows?.length) {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(chat.rows.map((row) => ({
          '카테고리': row.label,
          '카테고리 ID': row.category,
          '대화 수': row.conversations || 0,
          '고유 사용자': row.unique_users || 0,
          '모델 분포': JSON.stringify(row.models || {}),
        }))),
        '채팅 카테고리'
      );
    }

    if (revenueBasis) {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet([
          { '구분': 'IR 기준', '내용': revenueBasis.basis },
          { '구분': 'V3 공식', '내용': revenueBasis.v3_formula },
          { '구분': '회계 매출', '내용': revenueBasis.accounting_revenue?.meaning || '' },
        ]),
        'IR 금액 기준'
      );
    }

    XLSX.writeFile(wb, `WhatQuiz_Admin_Usage_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }, [chat, revenueBasis, weekly]);

  if (authLoading) {
    return <div style={styles.page}><div style={styles.container}>로딩 중...</div></div>;
  }
  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div style={styles.page}>
      <Helmet>
        <title>주간 사용량 | 왓퀴즈 Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div style={styles.container}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>주간 사용량</h1>
            <p style={styles.subtitle}>
              Firestore 완료 기록과 Cloud Run 로그 기준을 분리해서 봅니다. 기존 analytics 라우터와 별개로 새 관리자 지표 API를 사용합니다.
            </p>
          </div>
          <div style={styles.actions}>
            <select
              aria-label="기간"
              style={styles.select}
              value={period}
              onChange={(event) => setPeriod(event.target.value)}
            >
              {PERIOD_OPTIONS.map((option) => (
                <option key={option.key} value={option.key}>{option.label}</option>
              ))}
            </select>
            {period === 'custom' && (
              <>
                <input
                  aria-label="시작일"
                  style={styles.select}
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                />
                <input
                  aria-label="종료일"
                  style={styles.select}
                  type="date"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                />
              </>
            )}
            <button type="button" style={styles.ghostButton} onClick={loadData} disabled={loading}>
              {loading ? '갱신 중...' : '새로고침'}
            </button>
            <button type="button" style={styles.button} onClick={downloadExcel} disabled={!weekly && !chat}>
              엑셀 다운로드
            </button>
          </div>
        </div>

        {error && <div style={styles.error}>{error}</div>}
        {warnings.length > 0 && (
          <div style={styles.warning}>
            <strong>일부 집계 경고</strong>
            <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
              {warnings.map((message) => <li key={message}>{message}</li>)}
            </ul>
          </div>
        )}

        <div style={styles.grid}>
          {Object.entries(FEATURE_LABELS).map(([feature, label]) => (
            <div key={feature} style={styles.card}>
              <div style={styles.label}>{label}</div>
              <div style={styles.value}>{fmt(totals[feature]?.events)}</div>
              <div style={styles.note}>{weekly?.period_label || '선택 기간'} · 단위 {fmt(totals[feature]?.units)}</div>
            </div>
          ))}
        </div>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>채팅 9개 카테고리</h2>
          <p style={styles.note}>
            Firestore 대화 기록 기준입니다. 카테고리별 대화 수와 고유 사용자를 비교하기 쉽도록 꺾은선 그래프로 표시합니다.
          </p>
          <div style={styles.chart}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chatChartRows} margin={{ top: 12, right: 18, left: 0, bottom: 50 }}>
                <CartesianGrid stroke="#f2f4f6" vertical={false} />
                <XAxis dataKey="label" angle={-25} textAnchor="end" interval={0} tick={{ fill: '#6b7280', fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fill: '#6b7280', fontSize: 12 }} />
                <Tooltip
                  formatter={(value, name) => [fmt(value), name === 'conversations' ? '대화 수' : '고유 사용자']}
                />
                <Legend />
                <Line
                  type="linear"
                  dataKey="conversations"
                  name="대화 수"
                  stroke="#3182f6"
                  strokeWidth={2.5}
                  dot={{ r: 3, strokeWidth: 2 }}
                  activeDot={{ r: 5 }}
                  connectNulls
                />
                <Line
                  type="linear"
                  dataKey="unique_users"
                  name="고유 사용자"
                  stroke="#22c55e"
                  strokeWidth={2.5}
                  dot={{ r: 3, strokeWidth: 2 }}
                  activeDot={{ r: 5 }}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>카테고리</th>
                  <th style={{ ...styles.th, ...styles.num }}>대화</th>
                  <th style={{ ...styles.th, ...styles.num }}>고유 사용자</th>
                  <th style={styles.th}>상위 모델</th>
                </tr>
              </thead>
              <tbody>
                {(chat?.rows || []).map((row) => (
                  <tr key={row.category}>
                    <td style={styles.td}>{row.label}</td>
                    <td style={{ ...styles.td, ...styles.num }}>{fmt(row.conversations)}</td>
                    <td style={{ ...styles.td, ...styles.num }}>{fmt(row.unique_users)}</td>
                    <td style={styles.td}>{Object.entries(row.models || {}).slice(0, 3).map(([model, count]) => `${model} ${count}`).join(', ') || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>주간 기능 완료 기록</h2>
          <p style={styles.note}>
            {weekly?.source || 'Firestore 완료 기록 기준으로 집계합니다.'} 기능별 흐름을 비교하기 쉽도록 항상 꺾은선 그래프로 표시합니다.
          </p>
          <div style={styles.chart}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={weeklyChartRows} margin={{ top: 12, right: 18, left: 0, bottom: 8 }}>
                <CartesianGrid stroke="#f2f4f6" vertical={false} />
                <XAxis dataKey="week" tick={{ fill: '#6b7280', fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fill: '#6b7280', fontSize: 12 }} />
                <Tooltip
                  formatter={(value, name) => [fmt(value), FEATURE_LABELS[name] || name]}
                  labelFormatter={(label, payload) => payload?.[0]?.payload?.label || `주차 ${label}`}
                />
                <Legend formatter={(value) => FEATURE_LABELS[value] || value} />
                {Object.keys(FEATURE_LABELS).map((feature) => (
                  <Line
                    key={feature}
                    type="monotone"
                    dataKey={feature}
                    stroke={FEATURE_COLORS[feature]}
                    strokeWidth={2.5}
                    dot={{ r: 3, strokeWidth: 2 }}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>주차</th>
                  <th style={styles.th}>기간</th>
                  {Object.values(FEATURE_LABELS).map((label) => (
                    <th key={label} style={{ ...styles.th, ...styles.num }}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(weekly?.rows || []).map((row) => (
                  <tr key={row.week}>
                    <td style={styles.td}>{row.week}</td>
                    <td style={styles.td}>{row.label}</td>
                    {Object.keys(FEATURE_LABELS).map((feature) => (
                      <td key={feature} style={{ ...styles.td, ...styles.num }}>{fmt(row.features?.[feature]?.events)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>분석용 로그 기준</h2>
          <p style={styles.note}>
            지금 화면의 완료량은 Firestore 저장 기록 기준입니다. 요청 수, 오류율, 지연시간은 Cloud Run 로그를 BigQuery로 보내 request_id 기준으로 집계해야 가장 객관적입니다.
          </p>
          <div style={styles.methodGrid}>
            <div style={styles.methodCard}>
              <h3 style={styles.methodTitle}>1. 지금 바로 볼 수 있는 것</h3>
              <ul style={styles.methodList}>
                <li>기능 산출물이 실제 저장된 완료 수</li>
                <li>채팅 카테고리별 대화 수</li>
                <li>최근 오류와 파일 처리 실패 로그</li>
              </ul>
            </div>
            <div style={styles.methodCard}>
              <h3 style={styles.methodTitle}>2. 조심해야 할 것</h3>
              <ul style={styles.methodList}>
                <li>textPayload 문구 검색은 로그 문구가 바뀌면 누락됩니다.</li>
                <li>요청 시작, 완료, 오류 로그를 단순 합산하면 중복됩니다.</li>
                <li>장기 추세는 원본 로그 보관 기간과 쿼리 비용 영향을 받습니다.</li>
              </ul>
            </div>
            <div style={styles.methodCard}>
              <h3 style={styles.methodTitle}>3. 객관적 기준</h3>
              <ul style={styles.methodList}>
                {(logQuery?.objective_analysis?.recommended_source_of_truth || []).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
          <p style={styles.note}>{logQuery?.objective_analysis?.decision_rule}</p>
          <h3 style={styles.methodTitle}>Cloud Logging 복붙 쿼리</h3>
          {Object.entries(logQuery?.manual_queries || { feature_completion_signals: logQuery?.query || '로그 쿼리 로딩 중...' }).map(([name, query]) => (
            <div key={name} style={{ marginBottom: 12 }}>
              <p style={{ ...styles.note, marginBottom: 6 }}>{name}</p>
              <pre style={styles.codeBlock}>{query}</pre>
            </div>
          ))}
          <h3 style={styles.methodTitle}>BigQuery 전환 시 예시 SQL</h3>
          {Object.entries(logQuery?.example_bigquery_sql || {}).map(([name, sql]) => (
            <div key={name} style={{ marginBottom: 12 }}>
              <p style={{ ...styles.note, marginBottom: 6 }}>{name}</p>
              <pre style={styles.codeBlock}>{sql}</pre>
            </div>
          ))}
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>IR 금액 기준</h2>
          <p style={styles.note}>
            {revenueBasis?.basis || 'V3 발생주의 매출과 회계 현금주의 매출을 함께 봅니다.'}
            {revenueBasis?.v3_formula ? ` · ${revenueBasis.v3_formula}` : ''}
          </p>
        </section>
      </div>
    </div>
  );
};

export default UsageDashboard;
